import { Route } from '@angular/router';
import { initialDataResolver, publicDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { MsalGuard, MsalService } from '@azure/msal-angular';
import { LayoutComponent } from './core/layout/layout.component';
import { CanViewGuard } from './core/auth/guards/can-view.guard';
import { IsAdminGuard } from './core/auth/guards/is-admin.guard';
import { inject } from '@angular/core';
import { AuthService } from './core/auth/auth.service';
import { UserStore } from './core/services/user.store';
import { checkPublicPage } from './core/auth/guards/public-page';

export const appRoutes: Route[] = [
  // Embedded routes
  {
    path: 'embedded',
    canActivate: [MsalGuard, AuthGuard],
    canActivateChild: [MsalGuard],
    component: LayoutComponent,
    resolve: {
      initialData: initialDataResolver,
    },
    data: {
      layout: 'empty',
    },
    children: [
      {
        path: 'beheerders-instellingen',
        canActivate: [IsAdminGuard],
        loadChildren: () => import('app/features/admin/admin.routes'),
      },
      {
        path: ':name',
        canActivate: [CanViewGuard],
        loadChildren: () => import('app/features/admin/modules/modules.routes'),
      },
    ],
  },

  // Redirect empty path to '/dashboard'
  {
    path: '',
    pathMatch: 'full',
    redirectTo: () => {
      const authService = inject(AuthService);
      const msalService = inject(MsalService);
      const userStore = inject(UserStore);
      msalService.handleRedirectObservable().subscribe({
        next: result => {
          if (result) {
            authService.accessToken = result.accessToken;
            msalService.instance.setActiveAccount(result.account);
          }
        },
        error: error => {
          console.error('Redirect handling error:', error);
        },
      });

      if (msalService.instance.getAllAccounts().length > 0) {
        if (userStore.isOnboarding()) {
          return '/onboarding';
        }
        return '/dashboard';
      } else {
        return '/home';
      }
    },
  },
  // Error
  {
    path: 'error',
    children: [
      {
        path: '404',
        loadChildren: () => import('app/features/utility-pages/error/error-404/error-404.routes'),
      },
      {
        path: '500',
        loadChildren: () => import('app/features/utility-pages/error/error-500/error-500.routes'),
      },
    ],
  },

  // No-access landing for users without read-permission on the requested module
  {
    path: '',
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      {
        path: 'no-access',
        loadChildren: () => import('app/features/utility-pages/error/no-access/no-access.routes'),
      },
    ],
  },

  // Redirect signed-in user to the '/dashboard'
  //
  // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
  // path. Below is another redirection for that path to redirect the user to the desired
  // location. This is a small convenience to keep all main routes together here on this file.
  {
    path: 'signed-in-redirect',
    pathMatch: 'full',
    redirectTo: () => {
      const userStore = inject(UserStore);

      // Check if there's a saved redirect URL
      const redirectUrl = sessionStorage.getItem('redirectUrl');

      // Clear the saved URL
      if (redirectUrl) {
        sessionStorage.removeItem('redirectUrl');
        console.log('[signed-in-redirect]: Redirecting to saved URL:', redirectUrl);
        return redirectUrl;
      }

      // Default behavior
      if (userStore.isOnboarding()) {
        return '/onboarding';
      }
      return '/dashboard';
    },
  },

  // Auth routes for authenticated users
  {
    path: '',
    // canActivate: [MsalGuard],
    // canActivateChild: [MsalGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      {
        path: 'sign-out',
        loadChildren: () => import('app/features/utility-pages/auth/sign-out/sign-out.routes'),
      },
    ],
  },

  // Landing routes
  {
    path: '',
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      {
        path: 'home',
        loadChildren: () => import('app/features/public/home/home.routes'),
      },
    ],
  },

  // Dev-only componentgallerij: veldenbibliotheek in de echte cascade + branding.
  // Geen resolver: die blokkeert op modules die zonder login nooit laden. De gallerij
  // injecteert ThemeStore/SystemSettingsStore zelf (self-initializing, incl. localStorage-
  // cache), dus tenant-kleuren en dark mode werken ook zonder login — de pagina is daarmee
  // een deterministisch (screenshot)verificatiedoel. Buiten production geregistreerd zodat
  // prod-builds de route en chunk niet dragen.
  // Admin routes
  {
    path: '',
    canMatch: [checkPublicPage],
    resolve: {
      initialData: publicDataResolver,
    },
    component: LayoutComponent,
    children: [
      // Routes for public modules
      {
        path: ':name',
        loadChildren: () => import('app/features/admin/modules/modules.routes'),
      },
    ],
  },

  // Secured admin routes
  {
    path: '',
    canActivate: [AuthGuard],
    resolve: {
      initialData: initialDataResolver,
    },
    component: LayoutComponent,
    children: [
      {
        path: 'beheerders-instellingen',
        canActivate: [IsAdminGuard],
        loadChildren: () => import('app/features/admin/admin.routes'),
      },
      {
        path: 'demo',
        loadChildren: () => import('app/features/demo/demo.routes'),
      },
      {
        path: ':name',
        canActivate: [CanViewGuard],
        loadChildren: () => import('app/features/admin/modules/modules.routes'),
      },
    ],
  },
];
